import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * PremiumDivider \u2014 Hairline rule separator.
 *
 * Use between sections inside a card, between list rows, or as a labeled
 * meta-divider. Honors the `border-hairline` token, dark-mode safe.
 */

export interface PremiumDividerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical';
  /** Inset spacing (px) that matches the 8px grid */
  inset?: 0 | 4 | 8 | 12 | 16;
  /** Optional middle label (creates "Section \u2013 Label \u2013 Section" separator) */
  label?: React.ReactNode;
}

export const PremiumDivider = React.forwardRef<
  HTMLDivElement,
  PremiumDividerProps
>(
  (
    {
      orientation = 'horizontal',
      inset = 0,
      label,
      className,
      role = 'separator',
      ...props
    },
    ref,
  ) => {
    if (orientation === 'vertical') {
      return (
        <div
          ref={ref}
          role={role}
          aria-orientation="vertical"
          className={cn(
            'self-stretch w-px bg-border-hairline',
            inset > 0 && `mx-[${inset}px]`,
            className,
          )}
          {...props}
        />
      );
    }

    if (label) {
      return (
        <div
          ref={ref}
          role={role}
          className={cn('flex items-center gap-3', className)}
          {...props}
        >
          <span className="h-px flex-1 bg-border-hairline" aria-hidden="true" />
          <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            {label}
          </span>
          <span className="h-px flex-1 bg-border-hairline" aria-hidden="true" />
        </div>
      );
    }

    return (
      <div
        ref={ref}
        role={role}
        className={cn(
          'h-px w-full bg-border-hairline',
          inset > 0 && `my-[${inset}px]`,
          className,
        )}
        {...props}
      />
    );
  },
);
PremiumDivider.displayName = 'PremiumDivider';
